import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  CheckCircle,
  Loader2,
  Mail,
  ShieldCheck,
} from 'lucide-react';

// 作者入口：邮箱验证码登录（注册与登录同一条流程）+ 首次填写档案并接受总条款。
// 接口挂在 /api/studio/auth/* 之下 —— 换成 /api/author/* 会被 zone 上的
// catch-all worker sixteenwords-api 拦截返回 401，详见 studio/SETUP.md。

type Step = 'email' | 'code' | 'profile';

const RESEND_COOLDOWN = 60;

const AuthorAccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // 只接受站内相对路径，避免开放重定向。
  const rawNext = searchParams.get('next') || '/studio';
  const next = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/studio';

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [profile, setProfile] = useState({ name: '', phone: '', pen_name: '' });
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [busy, setBusy] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const codeInputRef = useRef<HTMLInputElement>(null);

  // 已登录且档案完整就不必再走一遍。
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/studio/auth/me', { credentials: 'same-origin' });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (data?.success && data.author?.profile_completed) {
            navigate(next, { replace: true });
            return;
          }
          if (data?.success) {
            setEmail(data.author?.email || '');
            setStep('profile');
          }
        }
      } catch {
        /* 网络问题就照常显示登录表单 */
      } finally {
        if (!cancelled) setCheckingSession(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, next]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus();
  }, [step]);

  const post = useCallback(async (path: string, body: unknown) => {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const contentType = res.headers.get('content-type') || '';
    if (contentType.indexOf('application/json') === -1) {
      throw new Error(`Server error: ${res.status} ${res.statusText}`);
    }
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, []);

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await post('/api/studio/auth/request-code', { email: email.trim() });
      setStep('code');
      setCooldown(RESEND_COOLDOWN);
      setNotice(`We sent a 6-digit code to ${email.trim()}. It expires in 10 minutes.`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const data = await post('/api/studio/auth/verify', { email: email.trim(), code: code.trim() });
      if (data.profile_completed) {
        navigate(next, { replace: true });
      } else {
        setNotice('');
        setStep('profile');
      }
    } catch (err: any) {
      setError(err.message);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await post('/api/studio/auth/profile', { ...profile, accept_terms: acceptTerms });
      navigate(next, { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const stepLabels: { key: Step; label: string }[] = [
    { key: 'email', label: 'Email' },
    { key: 'code', label: 'Verify' },
    { key: 'profile', label: 'Your details' },
  ];
  const stepIndex = stepLabels.findIndex((s) => s.key === step);

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-green-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Author Sign In - Woodlogos Studio</title>
        <meta
          name="description"
          content="Sign in to Woodlogos Author Studio to submit your manuscript and track its progress through editing, proofreading, and production."
        />
        <meta name="robots" content="noindex" />
        <link rel="canonical" href="https://www.woodlogos.net/author" />
      </Helmet>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <BookOpen className="h-7 w-7 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">Author Studio</h1>
        <p className="text-gray-600 text-center mb-8">
          Sign in with your email to submit a manuscript and follow its progress.
        </p>

        {/* 步骤指示 */}
        <div className="flex items-center justify-center mb-8">
          {stepLabels.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${
                    i < stepIndex
                      ? 'bg-green-600 border-green-600 text-white'
                      : i === stepIndex
                      ? 'bg-white border-green-600 text-green-700'
                      : 'bg-white border-gray-300 text-gray-400'
                  }`}
                >
                  {i < stepIndex ? <CheckCircle className="h-4 w-4" /> : i + 1}
                </div>
                <span
                  className={`ml-2 text-sm hidden sm:inline ${
                    i <= stepIndex ? 'text-gray-900 font-medium' : 'text-gray-400'
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < stepLabels.length - 1 && (
                <div className={`w-8 sm:w-10 h-0.5 mx-2 ${i < stepIndex ? 'bg-green-600' : 'bg-gray-300'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow rounded-2xl sm:px-10">
          {error && (
            <div className="mb-6 flex items-start bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}
          {notice && !error && (
            <div className="mb-6 flex items-start bg-green-50 border border-green-200 rounded-lg p-3">
              <Mail className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-green-800">{notice}</span>
            </div>
          )}

          {step === 'email' && (
            <form onSubmit={sendCode} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="you@example.com"
                />
                <p className="mt-2 text-sm text-gray-500">
                  No password needed — we'll email you a 6-digit code. New here? This creates your account.
                </p>
              </div>
              <button
                type="submit"
                disabled={busy || !email.trim()}
                className="w-full flex justify-center items-center py-2.5 px-4 rounded-lg text-white bg-green-600 hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Send code'}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={verifyCode} className="space-y-6">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                  6-digit code
                </label>
                <input
                  id="code"
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center text-2xl tracking-[0.5em] font-semibold focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="000000"
                />
              </div>
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className="w-full flex justify-center items-center py-2.5 px-4 rounded-lg text-white bg-green-600 hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Continue'}
              </button>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setCode('');
                    setError('');
                    setNotice('');
                  }}
                  className="inline-flex items-center text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Change email
                </button>
                <button
                  type="button"
                  disabled={busy || cooldown > 0}
                  onClick={() => sendCode()}
                  className="text-green-600 hover:text-green-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}

          {step === 'profile' && (
            <form onSubmit={saveProfile} className="space-y-5">
              <p className="text-sm text-gray-600">
                Signed in as <span className="font-medium text-gray-900">{email}</span>. We need a few
                details before you submit a manuscript.
              </p>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Legal name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label htmlFor="pen_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Pen name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  id="pen_name"
                  type="text"
                  value={profile.pen_name}
                  onChange={(e) => setProfile({ ...profile, pen_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Name to print on the book, if different"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  required
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="+1 555 000 1234"
                />
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <ShieldCheck className="h-4 w-4 text-green-600 mr-2" />
                  <span className="text-sm font-semibold text-gray-900">Terms of service</span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed mb-3">
                  Woodlogos processes your manuscript for editing, proofreading, and production. You keep
                  all rights to your work. We store your manuscript and contact details only to deliver
                  these services. You'll confirm your rights to each specific manuscript when you upload it.
                </p>
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    I have read and accept the terms of service.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={busy || !acceptTerms}
                className="w-full flex justify-center items-center py-2.5 px-4 rounded-lg text-white bg-green-600 hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Enter Studio'}
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          Questions? Email{' '}
          <a href="mailto:books@woodlogos.net" className="text-green-600 hover:text-green-700">
            books@woodlogos.net
          </a>
        </p>
      </div>
    </div>
  );
};

export default AuthorAccess;
